import { API_CONFIG } from './config';
import { AuthenticationError } from '../../shared/errors/AuthenticationError';
import { ValidationError } from '../../shared/errors/ValidationError';
import { LoginCredentials, LoginResponse, XanoUserResponse } from '../../types/auth';

/**
 * Service d'authentification utilisant l'API Xano
 *
 * Ce service gère toutes les opérations d'authentification :
 * - Connexion (login)
 * - Déconnexion (logout)
 * - Rafraîchissement du token
 * - Récupération des informations utilisateur
 *
 * @example
 * ```typescript
 * import { authService } from './auth.service';
 *
 * try {
 *   const response = await authService.login({ email: 'user@example.com', password: 'password' });
 *   console.log('Utilisateur connecté:', response.user);
 * } catch (error) {
 *   console.error('Erreur de connexion:', error.message);
 * }
 * ```
 */
class AuthService {
  /**
   * Tente de connecter un utilisateur avec son email et son mot de passe
   * @param credentials - Les identifiants de connexion (email et mot de passe)
   * @returns Une promesse résolue avec les données de l'utilisateur connecté
   * @throws {AuthenticationError} Si la connexion échoue
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      let data: any = {};
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new AuthenticationError(
          'Réponse invalide du serveur.',
          'INVALID_RESPONSE',
          jsonError as Error
        );
      }

      if (!response.ok) {
        // Gestion d'erreurs spécifiques Xano
        if (data?.code === 401 || response.status === 401) {
          throw new AuthenticationError(
            'Invalid credentials. Please check your email and password.',
            'INVALID_CREDENTIALS',
            new Error(data.message || 'Email ou mot de passe incorrect.')
          );
        }
        if (data?.code === 429 || response.status === 429) {
          throw new AuthenticationError(
            'Too many login attempts. Please try again later.',
            'TOO_MANY_REQUESTS',
            new Error(data.message || 'Trop de requêtes')
          );
        }

        // Erreur générique
        throw new AuthenticationError(
          data.message || 'Login failed. Please try again.',
          'LOGIN_FAILED',
          new Error(`HTTP ${response.status}: ${response.statusText}`)
        );
      }

      // Si la réponse est OK, on stocke le token et on retourne les données utilisateur
      if (data.authToken) {
        localStorage.setItem('authToken', data.authToken);
      }

      return data;
    } catch (error) {
      // Si c'est déjà une AuthenticationError, on la relance
      if (error instanceof AuthenticationError) {
        throw error;
      }
      // Sinon, on enveloppe l'erreur dans une AuthenticationError
      throw new AuthenticationError(
        error instanceof Error ? error.message : 'Login failed. Please try again.',
        'LOGIN_FAILED',
        error
      );
    }
  }

  /**
   * Récupère les informations de l'utilisateur connecté
   * @returns Les informations complètes de l'utilisateur depuis Xano
   * @throws {AuthenticationError} En cas d'erreur d'authentification
   */
  async getCurrentUser(): Promise<XanoUserResponse> {
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new AuthenticationError(
          'No authentication token found.',
          'NO_TOKEN',
          new Error('Missing token')
        );
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/${API_CONFIG.ENDPOINTS.AUTH.ME}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expiré ou invalide
          localStorage.removeItem('authToken');
          throw new AuthenticationError(
            'Session expired. Please log in again.',
            'TOKEN_EXPIRED',
            new Error('Invalid token')
          );
        }

        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new AuthenticationError(
          errorData.message || 'Unable to retrieve user information.',
          'USER_INFO_FAILED',
          new Error(`HTTP ${response.status}: ${response.statusText}`)
        );
      }

      const userData: XanoUserResponse = await response.json();
      return userData;
    } catch (error) {
      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw new AuthenticationError(
        error instanceof Error ? error.message : 'Error retrieving user information.',
        'USER_INFO_FAILED',
        error
      );
    }
  }

  /**
   * Déconnecte l'utilisateur en supprimant le token du stockage local
   */
  logout(): void {
    localStorage.removeItem('authToken');
  }

  /**
   * Vérifie si un utilisateur est actuellement authentifié
   * @returns true si un token d'authentification est présent, false sinon
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token && token !== 'debug-token' && token.length > 20;
  }

  /**
   * Récupère le token d'authentification actuel
   * @returns Le token d'authentification ou null s'il n'existe pas
   */
  getAuthToken(): string | null {
    try {
      return localStorage.getItem('authToken');
    } catch (error) {
      return null;
    }
  }
}

// Export de l'instance du service
export const authService = new AuthService();

// Export de l'ancienne façade pour la compatibilité
export const authFacade = authService;

